import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  NbMediaBreakpointsService,
  NbMenuItem,
  NbMenuService,
  NbSidebarService,
  NbThemeService,
} from '@nebular/theme';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { FirebaseUser } from '@vmspdb/ui/domain';
import { selectAuthUser } from '@vmspdb/ui/store';
import { filter, map, tap } from 'rxjs';
import { HeaderActionHostDirective } from '../../directives/header-action-host.directive';
import { LayoutRoutes } from '../../layouts.enum';
import { LayoutService } from '../../services/layout.service';

@UntilDestroy()
@Component({
  selector: 'vmspdb-ui-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
  private sidebarService = inject(NbSidebarService);
  private menuService = inject(NbMenuService);
  private themeService = inject(NbThemeService);
  private layoutService = inject(LayoutService);
  private breakpointService = inject(NbMediaBreakpointsService);
  private store = inject(Store);
  private route = inject(ActivatedRoute);

  @Input() sidebarId = 'sidebar';

  // public newMessages = new Array(4);
  // public newTasks = new Array(5);
  // public newNotifications = new Array(5);

  userPictureOnly = false;
  user?: FirebaseUser;

  layoutRoutes = LayoutRoutes;

  logo = 'assets/layout/images/logo-dark-small.svg';

  themes = [
    {
      value: 'default',
      name: 'Light',
    },
    {
      value: 'dark',
      name: 'Dark',
    },
    // {
    //   value: 'cosmic',
    //   name: 'Cosmic',
    // },
    // {
    //   value: 'corporate',
    //   name: 'Corporate',
    // },
  ];

  currentTheme = 'default';

  userMenu: NbMenuItem[] = [
    { title: 'Profile', icon: 'person-outline' },
    { title: 'Settings', icon: 'settings-outline' },
    { title: 'Log out', icon: 'lock-outline', link: '/auth/logout' },
  ];

  @ViewChild(HeaderActionHostDirective, { static: true })
  actionHost!: HeaderActionHostDirective;

  ngOnInit() {
    this.currentTheme = this.themeService.currentTheme;

    this.store
      .select(selectAuthUser)
      .pipe(
        untilDestroyed(this),
        tap((user) => (this.user = user)),
      )
      .subscribe();

    const { xl } = this.breakpointService.getBreakpointsMap();
    this.themeService
      .onMediaQueryChange()
      .pipe(
        untilDestroyed(this),
        map(([, currentBreakpoint]) => currentBreakpoint.width < xl),
        tap(
          (isLessThanXl) =>
            (this.logo = isLessThanXl
              ? 'assets/layout/images/logo-dark-minimal.png'
              : 'assets/layout/images/logo-dark-small.svg'),
        ),
      )
      .subscribe(
        (isLessThanXl: boolean) => (this.userPictureOnly = isLessThanXl),
      );

    // Auto-compact sidebar. See https://github.com/akveo/nebular/issues/2009#issuecomment-548988024
    this.menuService
      .onItemSelect()
      .pipe(
        untilDestroyed(this),
        filter((event) => event.tag === 'menu-sidebar'),
        tap(() => {
          if (document.documentElement.clientWidth < xl) {
            this.sidebarService.compact('menu-sidebar');
          }
        }),
      )
      .subscribe();

    this.themeService
      .onThemeChange()
      .pipe(
        untilDestroyed(this),
        map(({ name }) => name),
      )
      .subscribe((themeName) => (this.currentTheme = themeName));

    this.route.data
      .pipe(
        untilDestroyed(this),
        map(({ header }) => header),
        filter((data) => !!data),
        // tap((component) => console.log({ component })),
        tap((component) =>
          this.actionHost?.viewContainerRef?.createComponent(component),
        ),
      )
      .subscribe();

    // this.global.currentUser$
    //   .pipe(
    //     ,
    //     tap((usr) => (this.user = usr ?? {}))
    //   )
    //   .subscribe();
  }

  ngOnDestroy() {}

  changeTheme(themeName: string) {
    this.themeService.changeTheme(themeName);
  }

  toggleSidebar(): boolean {
    this.sidebarService.toggle(true, 'menu-sidebar');
    this.layoutService.changeLayoutSize();

    return false;
  }

  navigateHome() {
    this.menuService.navigateHome();
    return false;
  }

  log(activate: string, $event: any) {
    console.log({ activate, $event });
  }
}
